import { initializeApp, type FirebaseApp } from 'firebase/app'
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  type AppCheck,
} from 'firebase/app-check'
import { getAuth, type Auth } from 'firebase/auth'
import { getFirestore, type Firestore } from 'firebase/firestore'
import { getFunctions, type Functions } from 'firebase/functions'
import { getMessaging, isSupported, type Messaging } from 'firebase/messaging'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

function assertConfig() {
  const missing = Object.entries(firebaseConfig)
    .filter(([, value]) => !value || String(value).trim() === '')
    .map(([key]) => key)
  if (missing.length > 0) {
    throw new Error(
      `[firebase] Missing env for: ${missing.join(', ')}. Copy .env.example → .env and fill values.`,
    )
  }
}

assertConfig()

export const app: FirebaseApp = initializeApp(firebaseConfig)

const useDebugAppCheck =
  import.meta.env.DEV || import.meta.env.VITE_USE_APPCHECK_DEBUG === 'true'

if (useDebugAppCheck) {
  // Must be set before initializeAppCheck. Prefer a fixed UUID from .env
  // (also registered in Firebase Console → App Check → Manage debug tokens).
  // `true` auto-generates a new UUID each browser profile — useless unless registered.
  const configured = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN?.trim()
  const debugToken: string | true = configured && configured.length > 0 ? configured : true
  ;(globalThis as typeof globalThis & { FIREBASE_APPCHECK_DEBUG_TOKEN?: string | boolean }).FIREBASE_APPCHECK_DEBUG_TOKEN =
    debugToken
}

const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_ENTERPRISE_SITE_KEY?.trim()

export let appCheck: AppCheck | null = null
export let appCheckStatus: 'ready' | 'debug-only' | 'missing-site-key' = 'missing-site-key'

if (recaptchaSiteKey) {
  appCheck = initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(recaptchaSiteKey),
    isTokenAutoRefreshEnabled: true,
  })
  appCheckStatus = useDebugAppCheck ? 'debug-only' : 'ready'
} else if (useDebugAppCheck) {
  // Debug tokens still require a provider in many setups; without a site key we
  // skip init and surface status in the Phase 0 Firebase panel.
  appCheckStatus = 'missing-site-key'
  console.warn(
    '[firebase] App Check not initialized — set VITE_RECAPTCHA_ENTERPRISE_SITE_KEY after registering ' +
      'the "Biddle Web App" app in Firebase Console → App Check (reCAPTCHA Enterprise).',
  )
} else {
  console.warn('[firebase] Missing VITE_RECAPTCHA_ENTERPRISE_SITE_KEY. App Check is not initialized.')
}

export const auth: Auth = getAuth(app)
export const db: Firestore = getFirestore(app)

/** Auth, KYC, chat, biddle CRUD — matches iOS default Functions region. */
export const functionsUS: Functions = getFunctions(app, 'us-central1')

/** Search, letters, campaign ownership claims. */
export const functionsAU: Functions = getFunctions(app, 'australia-southeast2')

export const firebaseMeta = {
  projectId: firebaseConfig.projectId,
  appId: firebaseConfig.appId,
  appIdShort: firebaseConfig.appId.slice(-12),
  authDomain: firebaseConfig.authDomain,
  regions: {
    us: 'us-central1',
    au: 'australia-southeast2',
  },
  appCheckStatus,
  debugAppCheck: useDebugAppCheck,
} as const

/** Used in Phase 7 (web push). Safe to call earlier — returns null if unsupported. */
let messagingPromise: Promise<Messaging | null> | null = null

export function getMessagingIfSupported(): Promise<Messaging | null> {
  if (!messagingPromise) {
    messagingPromise = isSupported()
      .then((ok) => (ok ? getMessaging(app) : null))
      .catch(() => null)
  }
  return messagingPromise
}
